/**
 * 정산서 표(components/tabs/StatementSheet.vue)의 줄 만들기 — 순수 함수.
 *
 * 계획: ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 3-3절(파일 표)·4-5절(엑셀 파일 구성 — 정산서 영역은 이 모델을
 * 그대로 옮긴다). 화면과 엑셀이 같은 줄·같은 문구를 쓰도록 화면에서 떼어 냈다(1단계, 화면 변화 없음).
 *
 * 열: 항목 · 사용량 · 단가 · 금액. 부모 줄(STATEMENT_LINE_LABELS 순서: 월 사용료 · 120분 초과 통화 · 발신 · 웹 텍스트 · 회선 ·
 * 상담사 번호 추가 · 일회성 항목) 아래에 근거 줄(접지 않음), 맨 아래 공급가액 · 부가세 10% · 청구 합계(부가세 포함).
 *  - 셀은 언제나 `{ text, value }` — text 는 화면 문구 그대로, value 는 원 단위 숫자(숫자가 아닌 칸·문구뿐인 칸은 null).
 *    사용량 칸은 시간·건수라 value 가 늘 null 이다.
 *  - 금액 칸은 스냅숏 금액이다(`statement.lines.*.amount`, 없으면 `charge.lines.*.amount`, 상담사 번호 줄은 없으면 0).
 *    검토 필요 화면의 입력 폼(고치기·저장 안 됨)은 이 모델 밖이다 — StatementSheet.vue 가 그 상태에서 금액 칸만 덮는다.
 *    그래서 엑셀은 저장하지 않은 편집 값을 담지 않는다(서버 응답만).
 *  - 사람이 넣은 금액(`overridden`)은 `edited`('수정됨 · 계산값 X원', value = 계산값)·`memo` 를 채우고, 그 줄의 '계산' 근거 줄을
 *    '계산값 근거'로 바꾼다.
 *  - 옵션 `withNames`(기본 true): false 면 상담사 번호 '가장 많았던 날' 근거에 상담사 이름·번호 끝자리(`peak_numbers`)를 쓰지
 *    않고 'M/D n개'까지만 쓴다. 회선(070·대표번호) 끝자리는 회사 회선이라 그대로 둔다.
 *  - 옵션 `withCost`(기본 true): false 면 원가를 쓰는 근거 문구(웹 텍스트 'vox 메시지 요금'의 '원가 X원 …')를 원가 없는 문구로
 *    바꾼다. 화면은 둘 다 true, 내보내기는 withNames:false, 원가·마진을 고르지 않으면 withCost:false 도.
 *  - 두 옵션 모두 빠지면(undefined) true, true·false 가 아닌 값(null·0·'' 등)은 TypeError — 내보내기에서 이름·원가를 뺄지
 *    흐릿한 값을 짐작하지 않는다(sheetOptions.flagOption, 원가 · 마진 모델과 같은 판정).
 *  - 정산 시작일(계획서 4-3, 2·3단계 계약서 3-4) — 옵션을 늘리지 않고 상세 칸을 보고 스스로 판단한다. 시작일 칸이 없는 달의
 *    출력은 그대로다.
 *    · 정산 대상 아님(`billing_start.scope: before_start`): 항목·합계 없이 안내 한 줄(notBillableText).
 *    · 시작 월(`charge.lines.base.start_month` 있음): 월 사용료 사용량 '10/10~10/31 22/31일 일할'(개통 달 문구보다 우선)과
 *      일할 식, 포함 통화 일할 일수, 120분 초과 통화 끝에 '시작일 전 사용량'(usage.pre_start), 웹 텍스트에 시작일 분할 근거.
 *
 * vue·antd·xlsx 를 import 하지 않는다 — node --test 로 검증한다(service/__tests__/statementSheetModel.test.mjs).
 */
import {
  EMPTY,
  daysInMonth,
  formatDuration,
  formatNumber,
  formatUnitPrice,
  formatWon,
  isNumber,
  maskNumber,
} from './settlementFormat.js';
import {
  COUNSELOR_LINE_NO_RATE_TEXT,
  STATEMENT_LINE_LABELS,
  absentLineAmount,
  baseIncludesText,
  counselorNumberLineView,
  isInternalDetail,
  isLegacyStatementDetail,
  isNotBillableDetail,
  legacyPlanVersionOf,
  lineOverride,
  lineTypeLabel,
  oneOffAmountOf,
  outboundCountText,
  overageFormulaText,
  pairLines,
  statementStatusOf,
  summaryLine,
  unitsMinutesText,
  webUsageNotice,
} from './settlementRules.js';
import { preStartUsageText, settlementScopeView, shortDateText, startMonthWindowText } from './settlementStart.js';
import { flagOption } from './sheetOptions.js';

/** 행 종류 — 부모 항목 · 근거 줄 · 합계 줄 · 안내 줄(v70 이전 정산서) */
export const SHEET_ROW_KINDS = Object.freeze({ item: 'item', detail: 'detail', total: 'total', notice: 'notice' });

/** 화면 머리의 보조 문구 */
export const STATEMENT_SHEET_SUBTITLE = '공급가액 · 부가세 별도 · 월 사용료만 VAT 포함 금액에서 환산';

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const numOr = (v) => (isNumber(v) ? v : null);
// Usage is counted in 10-second units (units_10s) but shown as time — '단위' means nothing to the reader
const timeOf = (n) => (isNumber(n) ? unitsMinutesText(n) : EMPTY);

/**
 * 셀 하나
 * @param {string} text 화면 문구
 * @param {unknown} [value] 원 단위 숫자 — 숫자가 아니면 null
 * @returns {{ text: string, value: number|null }}
 */
export function sheetCell(text = '', value = null) {
  return { text: typeof text === 'string' ? text : String(text ?? ''), value: numOr(value) };
}

// ---------------------------------------------------------------------------
// Context — what every item reads from the detail
// ---------------------------------------------------------------------------

function contextOf(detail, { month, withNames, withCost }) {
  const d = detail ?? {};
  const legacy = isLegacyStatementDetail(d);
  const rates = d.rates ?? null;
  const charge = d.charge ?? null;
  const warnings = arr(d.warnings);
  return {
    detail: d,
    rates,
    charge,
    usage: d.usage ?? {},
    warnings,
    legacy,
    internal: isInternalDetail(d),
    status: statementStatusOf(d),
    /** The month has a default rate, so every item was computed */
    priced: !!rates && !!charge && !legacy,
    /** No default rate, but a human entered amounts (reply table 16) */
    manualOnly: !rates && !!charge,
    lines: legacy ? {} : charge?.lines ?? {},
    stLines: d.statement?.lines ?? null,
    dim: daysInMonth(month ?? d.month),
    /** The warning list already says the web usage is unverified — do not repeat it as a row pill */
    webNotice: warnings.some((w) => w?.code === 'WEB_USAGE_UNVERIFIED') ? null : webUsageNotice(d),
    withNames,
    withCost,
    /** Start month (start date not the 1st) — `charge.lines.base.start_month`; wins over the opening-month wording */
    startMonth: legacy ? null : obj(charge?.lines?.base?.start_month),
    /** Usage before the start date (`usage.pre_start`) — not billed */
    preStart: obj(d.usage?.pre_start),
    /** Web usage could not be split by the start date (no daily vox web summary) */
    webSplitMissing: warnings.some((w) => w?.code === 'WEB_START_SPLIT_UNAVAILABLE'),
  };
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * 근거 줄. `detail` 이 있으면 단가·금액 칸을 합친 설명 한 칸, 없으면(null) 단가·금액 칸이 따로 있다.
 * @param {{ name: string, usage?: string, detail?: string, unit?: string, unitValue?: unknown, amount?: string,
 *   amountValue?: unknown, tip?: string, pill?: object|null, override?: boolean }} c
 */
function detailRow(c) {
  const spans = c.detail !== undefined;
  return {
    kind: SHEET_ROW_KINDS.detail,
    level: 1,
    name: sheetCell(c.name),
    tip: c.tip ?? '',
    pill: c.pill ?? null,
    usage: sheetCell(c.usage ?? ''),
    detail: spans ? sheetCell(c.detail) : null,
    unit: spans ? null : sheetCell(c.unit ?? '', c.unitValue),
    amount: spans ? null : sheetCell(c.amount ?? '', c.amountValue),
    override: c.override === true,
  };
}

/** Parent item fields every item fills (amount cells come later) */
function itemBase(key, fields) {
  return {
    kind: SHEET_ROW_KINDS.item,
    level: 0,
    key,
    label: sheetCell(STATEMENT_LINE_LABELS[key]),
    usage: sheetCell(fields.usage ?? ''),
    usageSub: sheetCell(fields.usageSub ?? ''),
    unit: sheetCell(fields.unit ?? '', fields.unitValue),
    unitNote: sheetCell(fields.unitNote ?? ''),
    unitSub: sheetCell(fields.unitSub ?? '', fields.unitSubValue),
    unitMissing: fields.unitMissing === true,
    unitDim: fields.unitDim === true,
    override: fields.override === true,
    children: arr(fields.children).map(detailRow),
  };
}

function baseItem(ctx) {
  const { priced, rates } = ctx;
  const b = obj(ctx.lines.base);
  const fee = numOr(b?.monthly_vat_included);
  const inc = obj(b?.included_line);
  const sm = ctx.startMonth;
  const smWindow = startMonthWindowText(sm);
  let usageText = EMPTY;
  if (b && priced) {
    if (smWindow) usageText = smWindow;
    else if (b.opening_month) usageText = `개통 달 ${formatNumber(b.days)}일 / ${formatNumber(b.days_in_month)}일`;
    else if (inc) usageText = `포함 회선 ${maskNumber(inc.number_last4)} · ${formatNumber(inc.days)}일`;
    else usageText = b.warning === 'NO_PHONE_USAGE' ? '포함 회선·전화 없음' : '포함 회선 없음';
  }
  const children = [];
  if (b && isNumber(fee) && smWindow) {
    // Start month — prorated from max(start date, included line start) to the month end (decision 2 A)
    children.push({
      name: 'VAT 포함 → 공급가액',
      usage: '시작일부터 일할',
      detail: `⌊${formatNumber(fee)} × 10 × ${formatNumber(sm.days)} ÷ (11 × ${formatNumber(sm.month_days)})⌋ (원 미만 버림)`,
    });
    if (inc) {
      children.push({ name: '포함 회선', usage: `${maskNumber(inc.number_last4)} · ${formatNumber(inc.days)}일`, detail: '' });
    }
  } else if (b && isNumber(fee)) {
    const formula = b.opening_month
      ? `⌊${formatNumber(fee)} × 10 × ${formatNumber(b.days)} ÷ (11 × ${formatNumber(b.days_in_month)})⌋`
      : `${formatNumber(fee)} × 10 ÷ 11`;
    const supply = numOr(b.monthly_supply);
    children.push({
      name: 'VAT 포함 → 공급가액',
      usage: b.opening_month ? '개통 달 일할' : '',
      detail: supply !== null && !b.opening_month ? `${formula} = ${formatWon(supply)} (원 미만 버림)` : `${formula} (원 미만 버림)`,
    });
    if (b.opening_month && inc) {
      children.push({ name: '포함 회선', usage: `${maskNumber(inc.number_last4)} · ${formatNumber(inc.days)}일`, detail: '' });
    }
  }
  if (priced) children.push({ name: '포함 내용', usage: '', detail: baseIncludesText(rates) });
  const shownFee = priced && isNumber(fee);
  return itemBase('base', {
    usage: usageText,
    unit: priced ? (isNumber(fee) ? `월 ${formatNumber(fee)}원` : '미정') : EMPTY,
    unitValue: shownFee ? fee : null,
    unitNote: shownFee ? '(VAT 포함)' : '',
    unitMissing: priced && !isNumber(fee),
    override: b?.rate_source === 'override',
    children,
  });
}

function inboundItem(ctx) {
  const { priced, legacy, usage, lines } = ctx;
  const i = obj(lines.inbound);
  const ui = obj(usage.inbound);
  const wv = obj(usage.web_voice);
  const phone = numOr(i?.phone_units_10s) ?? numOr(ui?.units_10s);
  const web = numOr(i?.web_voice_units_10s) ?? numOr(wv?.units_10s);
  const used = numOr(i?.used_units_10s) ?? (phone !== null ? phone + (web ?? 0) : null);
  const included = numOr(i?.included_units_10s);
  const overage = numOr(i?.overage_units_10s);
  const price = numOr(i?.price_per_minute);
  const minutes = numOr(i?.included_minutes);
  const base = obj(lines.base);
  const counts = inboundCountsOf(ctx, i, ui, wv);
  const children = [];
  if (!legacy && (ui || i)) {
    children.push({
      name: '전화(수신)',
      usage: [timeOf(phone), isNumber(counts.phoneCalls) ? `${formatNumber(counts.phoneCalls)}통${counts.fullTag}` : '']
        .filter(Boolean)
        .join(' · '),
      detail: [
        isNumber(counts.ai) ? `AI ${formatDuration(counts.ai)}` : '',
        isNumber(counts.counselor) ? `상담사 ${formatDuration(counts.counselor)}` : '',
      ]
        .filter(Boolean)
        .join(' + ')
        .concat(counts.fullTag && (isNumber(counts.ai) || isNumber(counts.counselor)) ? counts.fullTag : '')
        .concat(' · 통화마다 더해 10초 올림'),
    });
    children.push({
      name: '웹 음성',
      usage: [timeOf(web), isNumber(counts.webCalls) ? `${formatNumber(counts.webCalls)}통${counts.webFullTag}` : '']
        .filter(Boolean)
        .join(' · '),
      detail: 'studio 웹 테스트 음성 · 통화마다 10초 올림',
      pill: ctx.webNotice,
    });
    if (priced) {
      children.push({
        name: '포함 통화',
        usage: isNumber(included) ? `− ${timeOf(included)}` : EMPTY,
        detail: includedBasisText(ctx, base, minutes),
      });
    }
    if (priced && isNumber(overage) && isNumber(price)) {
      children.push({ name: '계산', usage: '', detail: overageFormulaText(overage, price, i?.overage_amount) });
    }
  }
  // Start month — usage before the start date is not billed (shown last, whatever the options)
  const preText = legacy ? '' : preStartUsageText(ctx.preStart);
  if (preText) children.push({ name: '시작일 전 사용량', usage: '', detail: preText });
  const showOverage = priced && isNumber(overage);
  return itemBase('inbound', {
    usage: showOverage ? `초과 ${timeOf(overage)}` : used === null ? EMPTY : `사용 ${timeOf(used)}`,
    unit: priced ? (isNumber(price) ? `1분당 ${formatUnitPrice(price)}원` : '미정') : EMPTY,
    unitValue: priced ? price : null,
    unitMissing: priced && !isNumber(price),
    override: i?.price_per_minute_rate_source === 'override' || i?.included_minutes_rate_source === 'override',
    children,
  });
}

/**
 * 120분 초과 통화 근거 줄의 통화 수·초. 시간(phone_units_10s)은 청구분이므로 시작 월이면 통화 수·초도 청구분 칸
 * (charge.lines.inbound.phone_calls·phone_ai_seconds·phone_counselor_talk_seconds·web_voice_calls — 계약서 1-2 [4단계 확인])을
 * 쓴다. 시작 월인데 그 칸이 없으면 달 전체 값(usage)에 '(달 전체)'를 붙여 시간과 다른 모집단임을 밝힌다. 시작 월이 아니면 usage 그대로.
 */
function inboundCountsOf(ctx, i, ui, wv) {
  const split = !!(ctx.startMonth || ctx.preStart);
  const full = { phoneCalls: numOr(ui?.calls), ai: numOr(ui?.ai_seconds), counselor: numOr(ui?.counselor_talk_seconds), webCalls: numOr(wv?.calls) };
  if (!split) return { ...full, fullTag: '', webFullTag: '' };
  const billedPhone = isNumber(i?.phone_calls);
  const billedWeb = isNumber(i?.web_voice_calls);
  return {
    phoneCalls: billedPhone ? i.phone_calls : full.phoneCalls,
    ai: billedPhone ? numOr(i?.phone_ai_seconds) : full.ai,
    counselor: billedPhone ? numOr(i?.phone_counselor_talk_seconds) : full.counselor,
    webCalls: billedWeb ? i.web_voice_calls : full.webCalls,
    fullTag: billedPhone ? '' : '(달 전체)',
    webFullTag: billedWeb ? '' : '(달 전체)',
  };
}

/** 포함 통화 근거 — 시작 월 일할이 개통 달 일할보다 우선 */
function includedBasisText(ctx, base, minutes) {
  const sm = ctx.startMonth;
  if (startMonthWindowText(sm) && isNumber(minutes)) {
    return `${formatNumber(minutes)}분 × ${formatNumber(sm.days)}/${formatNumber(sm.month_days)}일 일할(올림) · 시작일부터`;
  }
  return base?.opening_month && isNumber(minutes)
    ? `${formatNumber(minutes)}분 × ${formatNumber(base.days)}/${formatNumber(base.days_in_month)}일 일할(올림)`
    : '월 사용료에 포함';
}

function outboundItem(ctx) {
  const { priced, legacy, internal, usage, lines } = ctx;
  const o = obj(lines.outbound);
  const uo = obj(usage.outbound);
  // Legacy usage has no outbound block — only the count from `usage.calls.outbound`
  const calls = numOr(uo?.calls) ?? numOr(usage.calls?.outbound);
  const dialed = numOr(o?.dialed) ?? numOr(uo?.dialed);
  const answered = numOr(o?.answered) ?? numOr(uo?.answered);
  const units = numOr(o?.units_10s) ?? numOr(uo?.units_10s);
  const perCall = numOr(o?.per_call);
  const unitPrice = numOr(o?.unit_price);
  const none = !calls && !dialed;
  // Calls cancelled before dialing or failed to dial (0원) keep their count
  const counts = outboundCountText({ calls, dialed });
  const children = [];
  if (!none && (o || uo) && !legacy) {
    const callsShown = priced && isNumber(dialed) && isNumber(perCall) && !internal;
    const talkShown = priced && isNumber(units) && isNumber(unitPrice) && !internal;
    children.push({
      name: '발신 건당',
      tip: '실제로 건 통화(무응답·음성사서함 포함) · 걸기 전 취소·발신 실패는 0원',
      usage: counts.perCall,
      unit: priced ? (isNumber(perCall) ? `${formatUnitPrice(perCall)}원/건` : '미정') : EMPTY,
      unitValue: priced ? perCall : null,
      override: o?.per_call_rate_source === 'override',
      amount: callsShown
        ? `${formatNumber(dialed)}건 × 건당 ${formatUnitPrice(perCall)}원 = ${formatWon(o?.calls_amount)}`
        : EMPTY,
      amountValue: callsShown ? o?.calls_amount : null,
    });
    children.push({
      name: '받은 발신 통화',
      tip: '환자가 받은 통화만 · 통화마다 AI + 상담사 통화를 더해 10초 올림',
      usage: `${formatNumber(answered)}통 · ${timeOf(units)}${isNumber(uo?.ai_seconds) ? ` (AI ${formatDuration(uo.ai_seconds)})` : ''}`,
      unit: priced ? (isNumber(unitPrice) ? `${formatUnitPrice(unitPrice)}원/10초` : '미정') : EMPTY,
      unitValue: priced ? unitPrice : null,
      override: o?.unit_price_rate_source === 'override',
      amount: talkShown ? `${timeOf(units)} × 10초당 ${formatUnitPrice(unitPrice)}원 = ${formatWon(o?.talk_amount)}` : EMPTY,
      amountValue: talkShown ? o?.talk_amount : null,
    });
  }
  const unitShown = priced && !none;
  return itemBase('outbound', {
    usage: none ? '발신 없음' : counts.total,
    usageSub: none ? '' : `받은 통화 ${formatNumber(answered)}통 · ${timeOf(units)}`,
    unit: unitShown ? (isNumber(perCall) ? `${formatUnitPrice(perCall)}원/건` : '미정') : EMPTY,
    unitValue: unitShown ? perCall : null,
    unitSub: unitShown && isNumber(unitPrice) ? `${formatUnitPrice(unitPrice)}원/10초` : '',
    unitSubValue: unitShown ? unitPrice : null,
    unitMissing: unitShown && !isNumber(perCall),
    override: o?.per_call_rate_source === 'override' || o?.unit_price_rate_source === 'override',
    children,
  });
}

/** 웹 텍스트 근거 문구 — withCost:false 면 원가 금액을 쓰지 않는다 */
function webTextBasisText(passThrough, webCost, withCost) {
  if (!withCost) return passThrough === false ? '청구하지 않음' : '원가 그대로 청구';
  return passThrough === false ? `원가 ${formatWon(webCost)} · 청구하지 않음` : `원가 ${formatWon(webCost)} 그대로 청구`;
}

/**
 * 시작 월 웹 테스트 근거 — 웹(텍스트·음성)은 vox 일별 칸으로 시작일 전·후를 나눈다(결정 3). 일별 칸이 없으면 나누지 못해
 * WEB_START_SPLIT_UNAVAILABLE(차단)이다. 시작 월이 아니면 null.
 */
function webStartSplitRow(ctx) {
  if (!ctx.startMonth && !ctx.preStart) return null;
  if (ctx.webSplitMissing) {
    return {
      name: '시작일 분할',
      usage: '',
      detail: '웹 일별 기록이 없어 시작일 전·후로 나누지 못했습니다 · 대사 탭에서 vox 를 다시 읽으면 나뉩니다',
    };
  }
  const web = numOr(ctx.preStart?.web_calls);
  if (!web) return null;
  return {
    name: '시작일 전 웹 테스트',
    usage: `${formatNumber(web)}통`,
    detail: '청구 제외 · vox 일별 기록으로 시작일 전·후를 나눔(웹 음성 포함)',
  };
}

function webTextItem(ctx) {
  const { priced, legacy, usage, lines, detail } = ctx;
  const w = obj(lines.web_text);
  const uw = obj(usage.web_text);
  const calls = numOr(w?.calls) ?? numOr(uw?.calls);
  // Start month — the billed amount covers the web text after the start date only, so the basis names the billed cost
  // (cost.billable.split.web_text = charge.lines.web_text.vox_cost), not the whole month
  const billedCost = ctx.startMonth || ctx.preStart ? numOr(detail.cost?.billable?.split?.web_text) ?? numOr(w?.vox_cost) : null;
  const webCost = billedCost ?? numOr(detail.cost?.split?.web_text) ?? numOr(uw?.vox_cost);
  const passThrough = typeof w?.pass_through === 'boolean' ? w.pass_through : null;
  let unit = EMPTY;
  if (priced) unit = passThrough === false ? '청구 안 함' : passThrough === true ? '원가 그대로' : '미정';
  const children = [];
  if (!legacy && (calls || webCost)) {
    children.push({
      name: 'vox 메시지 요금',
      usage: `${formatNumber(calls)}통`,
      detail: webTextBasisText(passThrough, webCost, ctx.withCost),
      pill: ctx.webNotice,
    });
  }
  const split = legacy ? null : webStartSplitRow(ctx);
  if (split) children.push(split);
  return itemBase('web_text', {
    usage: `${formatNumber(calls)}통`,
    unit,
    unitMissing: priced && passThrough === null,
    override: w?.rate_source === 'override',
    children,
  });
}

function lineItem(ctx) {
  const { priced, internal, usage, lines, dim } = ctx;
  const used = arr(usage.lines);
  const l = obj(lines.line);
  const items = priced ? arr(l?.items) : [];
  // Same pairing as the '원가 · 마진' table — a charge item without a usage line is drawn too
  const pairs = pairLines(used, items);
  const children = pairs.map(({ u, item }) => {
    const included = item?.included === true;
    const price = numOr(item?.monthly);
    const missing = !!item && !included && price === null;
    let unit = EMPTY;
    let unitValue = null;
    let amount = EMPTY;
    let amountValue = null;
    if (item) {
      if (included) {
        unit = '월 사용료에 포함';
        amount = '0원';
        amountValue = 0;
      } else if (missing) {
        unit = '판매가 미정';
        amount = '금액에서 뺌';
      } else {
        unit = `월 ${formatNumber(price)}원${item.prorate === true ? ' · 일할' : ''}`;
        unitValue = price;
        amount = internal ? '0원' : formatWon(item.amount);
        amountValue = internal ? 0 : item.amount;
      }
    }
    let lineUsage = EMPTY;
    // Start month — the amount is prorated on the billed days (charge item days, from the start date), not the month's
    // usage days, so the row shows the billed days
    const billedDays = ctx.startMonth && isNumber(item?.days) ? item.days : null;
    if (!u) lineUsage = '이 달 회선 사용 기록 없음';
    else if (billedDays !== null && dim) lineUsage = `${formatNumber(billedDays)}일 / ${dim}일 (시작일부터)`;
    else if (isNumber(u.days) && dim) lineUsage = `${formatNumber(u.days)}일 / ${dim}일`;
    return {
      name: `${lineTypeLabel(u?.type ?? item?.type)} ${maskNumber(u?.number_last4 ?? item?.number_last4)}`,
      usage: lineUsage,
      unit,
      unitValue,
      override: item?.rate_source === 'override',
      amount,
      amountValue,
    };
  });
  const extra = items.filter((it) => it?.included !== true).length;
  let usageText = '이 달 회선 없음';
  if (pairs.length) {
    if (!priced) usageText = `${formatNumber(pairs.length)}개`;
    else usageText = extra ? `추가·대표번호 ${formatNumber(extra)}개` : '포함 회선만';
  }
  return itemBase('line', {
    usage: usageText,
    usageSub: priced && pairs.length ? '070 1회선은 월 사용료에 포함' : '',
    unit: priced && pairs.length ? '회선별' : EMPTY,
    unitDim: true,
    override: false,
    children,
  });
}

/**
 * 상담사 번호 줄 보기 — withNames:false 면 가장 많았던 날의 상담사 이름·번호 끝자리(`peak_numbers`)를 빼고 읽는다
 * ('10/14 5개'까지만, '기록에 처음 n개만 남음'도 붙지 않는다)
 */
function counselorView(detail, withNames) {
  const line = obj(detail?.charge?.lines?.counselor_number);
  if (withNames || !line) return counselorNumberLineView(detail);
  return counselorNumberLineView({
    ...detail,
    charge: { ...detail.charge, lines: { ...detail.charge.lines, counselor_number: { ...line, peak_numbers: [] } } },
  });
}

function counselorNumberItem(ctx) {
  const { priced, lines } = ctx;
  const v = counselorView(ctx.detail, ctx.withNames);
  const line = obj(lines.counselor_number);
  const evidence = v.state === 'applies' || v.state === 'no_calls';
  let usageText = EMPTY;
  if (priced) {
    if (isNumber(line?.peak_count) && (evidence || line.peak_count > 0)) {
      const extra = numOr(line?.peak_extra) ?? 0;
      usageText = `이 달 최대 ${formatNumber(line.peak_count)}개${extra > 0 ? ` · 초과 ${formatNumber(extra)}개` : ''}`;
    } else if (!evidence && v.state !== 'no_rate') {
      // shell / absent — "no evidence" note (no_rate says it in the unit cell)
      usageText = v.noteText || EMPTY;
    }
  }
  const children = [];
  if (priced) {
    if (v.segmentsText) children.push({ name: '구간', usage: '', detail: v.segmentsText });
    if (v.formulaText) children.push({ name: '계산', usage: '', detail: v.formulaText });
    if (v.state === 'no_calls' && v.noteText) children.push({ name: '통화 없음', usage: '', detail: v.noteText });
    if (v.windowText) {
      children.push({
        name: '청구 창',
        tip: ctx.startMonth
          ? `시작 월 — 정산 시작일(${shortDateText(ctx.startMonth.start_date) || '시작일'})과 포함 070 회선이 켜진 날 중 늦은 날부터 그 달 끝`
          : '개통 달 — 포함 070 회선이 켜진 날부터 그 달 끝',
        usage: '',
        detail: v.windowText,
      });
    }
    if (v.gapText) {
      children.push({
        name: '매시 기록 없는 날',
        tip: '그날 쓴 값은 번호를 추가할 때의 기록, 없으면 0 · 앞뒤 기록은 참고값',
        usage: '',
        detail: v.gapText,
      });
    }
    if (v.peakText) children.push({ name: '가장 많았던 날', usage: '', detail: v.peakText });
  }
  let unit = EMPTY;
  if (priced) unit = evidence && v.unitText ? v.unitText : v.state === 'no_rate' ? COUNSELOR_LINE_NO_RATE_TEXT : EMPTY;
  return itemBase('counselor_number', {
    usage: usageText,
    unit,
    unitDim: v.state === 'no_rate',
    override: v.rateSource === 'override',
    children,
  });
}

// ---------------------------------------------------------------------------
// Amounts (snapshot — the review editor is the component's)
// ---------------------------------------------------------------------------

/** Snapshot amount — statement item, else charge item, else absent (counselor-number item → 0) */
function snapshotAmount(ctx, key) {
  const { stLines, lines } = ctx;
  return (
    numOr(stLines?.[key]?.amount) ??
    numOr(lines?.[key]?.amount) ??
    (stLines?.[key] || lines?.[key] ? null : absentLineAmount(key))
  );
}

/** Human-entered amount in a snapshot (never in a live estimate) */
function snapshotOverride(ctx, key, amount) {
  const { charge, internal, legacy, priced } = ctx;
  if (!charge || internal || legacy || charge.source === 'estimate') return null;
  const computedValue = priced ? numOr(ctx.lines?.[key]?.estimate) : null;
  const ov = lineOverride(key, { amount, computed: computedValue, statementLines: ctx.stLines });
  return ov.overridden ? ov : null;
}

/**
 * 금액 칸 — amount · amountDim · edited(수정됨 문구, value = 계산값) · memo
 * @returns {{ amount: {text: string, value: number|null}, amountDim: boolean, edited: {text: string, value: number|null},
 *   memo: {text: string, value: null} }}
 */
function snapshotAmountCells(ctx, key) {
  const none = { edited: sheetCell(''), memo: sheetCell('') };
  if (ctx.internal) return { ...none, amount: sheetCell('0원', 0), amountDim: true };
  const amount = snapshotAmount(ctx, key);
  const ov = snapshotOverride(ctx, key, amount);
  // Without a default rate only human amounts mean anything — the server 0 means "not computed"
  if (!ctx.priced && !ov) return { ...none, amount: sheetCell(EMPTY), amountDim: true };
  let edited = sheetCell('');
  if (ov) {
    edited =
      isNumber(ov.estimate) && ctx.priced
        ? sheetCell(`수정됨 · 계산값 ${formatWon(ov.estimate)}`, ov.estimate)
        : sheetCell('수정됨');
  }
  return {
    amount: sheetCell(formatWon(amount), amount),
    amountDim: amount === 0,
    edited,
    memo: sheetCell(ov?.memo ?? ''),
  };
}

/**
 * 사람이 넣은 금액의 근거 줄 — 계산이 그 금액을 설명하지 않으니 '계산' 줄이 계산값의 근거가 된다
 * @param {Array<object>} children 근거 줄(detailRow 모양)
 * @returns {Array<object>}
 */
export function editedBasisRows(children) {
  return arr(children).map((c) =>
    c.name?.text === '계산'
      ? {
          ...c,
          name: sheetCell('계산값 근거'),
          detail: sheetCell(`${c.detail?.text ?? ''} (계산값, 청구 금액은 직접 입력)`),
        }
      : c
  );
}

function oneOffItem(ctx) {
  // One-off items — the list itself is the 일회성 항목 section below (no basis rows here)
  const oneOffLines = ctx.charge?.lines?.one_off ?? ctx.stLines?.one_off ?? null;
  const count = arr(oneOffLines?.items).length;
  const oneOff = oneOffAmountOf(ctx.detail);
  const shown = ctx.internal ? 0 : oneOff;
  return {
    ...itemBase('one_off', {
      usage: count ? `${formatNumber(count)}건` : '없음',
      usageSub: count ? '아래 일회성 항목 목록' : '',
      unit: EMPTY,
      unitDim: true,
      override: false,
      children: [],
    }),
    amount: sheetCell(formatWon(shown), shown),
    amountDim: !oneOff || ctx.internal,
    edited: sheetCell(''),
    memo: sheetCell(''),
    muted: !oneOff,
  };
}

// ---------------------------------------------------------------------------
// Sheet
// ---------------------------------------------------------------------------

/** v70-legacy row — a confirmed month is reopened before it can be recalculated */
function legacyText(status) {
  return `요금표 v70 이전 정산서라 항목별 금액이 없습니다. ${
    status === 'confirmed' ? '[재수정] → [다시 계산]' : '[다시 계산]'
  }한 뒤에 보입니다.`;
}

function noticeOf(ctx) {
  if (ctx.legacy || ctx.priced) return null;
  const link = legacyPlanVersionOf(ctx.warnings) ? '기본 요율 다시 저장하기 →' : '기본 요율 만들기 →';
  return ctx.manualOnly
    ? { text: '계산값 없음 · 사람이 넣은 금액만 합했습니다', link, warn: true }
    : { text: '계산값 없음', link, warn: true };
}

function totalRow(key, label, note, amount) {
  return { kind: SHEET_ROW_KINDS.total, level: 0, key, label: sheetCell(label), note: sheetCell(note), amount: sheetCell(formatWon(amount), amount) };
}

/**
 * 정산서 표 모델
 * @param {object} detail 정산 상세(요청 계약 2)
 * @param {{ month?: string, summary?: object, withNames?: boolean, withCost?: boolean, editedKeys?: Iterable<string>|null }} [opts]
 *   month: 'YYYY-MM'(회선 일할의 그 달 일수, 없으면 detail.month). summary: settlementRules.summaryLine(detail)(없으면 여기서 계산).
 *   editedKeys: '계산' 근거 줄을 '계산값 근거'로 바꿀 항목 — 없으면(null) 스냅숏의 사람이 넣은 금액 항목. 검토 필요 화면이
 *   입력 폼의 고친 항목을 넘긴다.
 * @returns {{
 *   status: string, legacy: boolean, internal: boolean, priced: boolean, manualOnly: boolean,
 *   legacyText: string, notice: {text: string, link: string, warn: boolean}|null, internalNote: string,
 *   items: Array<object>, totals: Array<object>, notBillable: boolean, notBillableText: string, startMonth: boolean
 * }}
 *   notBillable(정산 시작일 전 달): items·totals 가 비고 notBillableText 한 줄. startMonth: charge.lines.base.start_month 가 있다
 *   items: 부모 줄(kind 'item', level 0) — key · label · usage · usageSub · unit · unitNote · unitSub · amount · edited · memo
 *   (모두 셀) · unitMissing · unitDim · override · amountDim · muted · children(kind 'detail', level 1 — name · usage 셀, 그리고
 *   detail 셀 또는 unit·amount 셀 · tip · pill · override). totals: kind 'total' — key(supply·vat·with_vat) · label · note · amount.
 */
export function buildStatementSheet(detail, opts = {}) {
  const { month, summary, editedKeys = null } = opts;
  // 빠지면 true, true·false 가 아니면 던진다(sheetOptions.flagOption — 원가 · 마진 모델과 같은 판정)
  const ctx = contextOf(detail, { month, withNames: flagOption(opts, 'withNames'), withCost: flagOption(opts, 'withCost') });
  const edited = editedKeys ? new Set(editedKeys) : null;

  if (isNotBillableDetail(ctx.detail)) {
    // Before the billing start date — nothing is billed, so no items and no totals (one notice row)
    const { startDate } = settlementScopeView(ctx.detail);
    return {
      status: ctx.status,
      legacy: false,
      internal: ctx.internal,
      priced: false,
      manualOnly: false,
      legacyText: '',
      notice: null,
      internalNote: '',
      items: [],
      totals: [],
      notBillable: true,
      notBillableText: `정산 시작일${startDate ? `(${startDate})` : ''} 전 달이라 정산하지 않습니다.`,
      startMonth: false,
    };
  }

  const items = ctx.legacy
    ? []
    : [baseItem, inboundItem, outboundItem, webTextItem, lineItem, counselorNumberItem].map((make) => {
        const item = make(ctx);
        const cells = snapshotAmountCells(ctx, item.key);
        const isEdited = edited ? edited.has(item.key) : !!cells.edited.text;
        return {
          ...item,
          children: isEdited ? editedBasisRows(item.children) : item.children,
          ...cells,
          muted: cells.amount.text === '0원',
        };
      });
  items.push(oneOffItem(ctx));

  const s = summary ?? summaryLine(ctx.detail);
  const internalNote = ctx.internal ? '내부·테스트 회사는 청구가 0원입니다' : '';
  return {
    status: ctx.status,
    legacy: ctx.legacy,
    internal: ctx.internal,
    priced: ctx.priced,
    manualOnly: ctx.manualOnly,
    legacyText: ctx.legacy ? legacyText(ctx.status) : '',
    notice: noticeOf(ctx),
    internalNote,
    items,
    totals: [
      totalRow('supply', '공급가액', internalNote, s?.supply?.amount),
      totalRow('vat', '부가세 10%', '원 미만 버림', s?.vat),
      totalRow('with_vat', '청구 합계 (부가세 포함)', '', s?.totalWithVat),
    ],
    notBillable: false,
    notBillableText: '',
    startMonth: !!ctx.startMonth,
  };
}

/**
 * 표를 한 줄씩 편 목록 — 엑셀 정산서 영역이 위에서부터 그대로 쓴다. v70 이전 정산서면 맨 앞에 안내 줄(kind 'notice').
 * @param {ReturnType<typeof buildStatementSheet>} sheet
 * @returns {Array<object>} 부모 줄 다음에 그 근거 줄, 마지막에 합계 줄 셋
 */
export function statementSheetRows(sheet) {
  const out = [];
  if (sheet?.notBillable) {
    return [{ kind: SHEET_ROW_KINDS.notice, level: 0, key: 'not_billable', text: sheetCell(sheet.notBillableText) }];
  }
  if (sheet?.legacy) out.push({ kind: SHEET_ROW_KINDS.notice, level: 0, key: 'legacy', text: sheetCell(sheet.legacyText) });
  for (const item of arr(sheet?.items)) {
    out.push(item);
    item.children.forEach((child, i) => out.push({ ...child, key: `${item.key}.${i}` }));
  }
  out.push(...arr(sheet?.totals));
  return out;
}

// ---------------------------------------------------------------------------
// Model rows → StatementSheet.vue template rows (vue 없음 — 화면과 시험이 같은 함수를 쓴다)
// ---------------------------------------------------------------------------

/**
 * 근거 줄 → 화면 줄. 템플릿은 `detail !== undefined` 면 설명 한 칸 줄, 아니면 단가·금액 칸 줄로 그린다.
 * @param {object} child buildStatementSheet 의 items[].children[]
 * @returns {{ name: string, tip: string, pill: object|null, usage: string, detail: string|undefined, unit: string,
 *   amount: string, override: boolean }}
 */
export function sheetChildView(child) {
  return {
    name: child.name.text,
    tip: child.tip,
    pill: child.pill,
    usage: child.usage.text,
    detail: child.detail ? child.detail.text : undefined,
    unit: child.unit?.text ?? '',
    amount: child.amount?.text ?? '',
    override: child.override,
  };
}

/**
 * 부모 줄 → 화면 줄(검토 입력 폼 칸은 비운 모양 — 검토 필요 화면은 StatementSheet.vue 가 금액 칸만 덮는다).
 * @param {object} item buildStatementSheet 의 items[]
 * @returns {object}
 */
export function sheetItemView(item) {
  return {
    key: item.key,
    label: item.label.text,
    usage: item.usage.text,
    usageSub: item.usageSub.text,
    unit: item.unit.text,
    unitNote: item.unitNote.text,
    unitSub: item.unitSub.text,
    unitMissing: item.unitMissing,
    unitDim: item.unitDim,
    override: item.override,
    children: item.children.map(sheetChildView),
    amountText: item.amount.text,
    amountDim: item.amountDim,
    editedText: item.edited.text,
    memo: item.memo.text,
    unsaved: false,
    editing: false,
    fixable: false,
    formAmount: null,
    formMemo: '',
    estimate: null,
    edited: false,
    muted: item.muted,
  };
}
